import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";

import InsuranceDetailsErrorModal from "../../../components/modals/insuranceDetailsErrorModal/insuranceDetailsErrorModal";
import InsuranceModal from "../../../components/modals/insuranceModal/insuranceModal";
import { usePharmaFormFields } from "../../../context/pharmaFormFieldsContext";
import { NycbsPharmaOrderKeys } from "../../../enums/nycbsPharmaOrder";
import { RootState } from "../../../redux/store/store";
import { findLabelFromGivenState } from "../utils/stateOptions";

interface InsuranceInfoBannerProps {
  docId: string;
}

const InsuranceInfoBanner = (props: InsuranceInfoBannerProps) => {
  const { data: singleCmmOrderData } = useSelector(
    (state: RootState) => state.cmmSingleOrder,
  );
  const [showModal, setShowModal] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const { formFieldsData } = usePharmaFormFields();

  useEffect(() => {
    showModal && openModal("insurance-details-error-modal");
    showInsuranceModal && openModal("insurance-modal");
  }, [showModal, showInsuranceModal]);

  const checkIfAnyInsuranceDataMising = () => {
    if (
      formFieldsData?.[NycbsPharmaOrderKeys.FormName]?.["filledValue"] === "" ||
      formFieldsData?.[NycbsPharmaOrderKeys.PatientInsuranceState]?.[
        "filledValue"
      ] === ""
    ) {
      return true;
    }

    if (
      formFieldsData?.[NycbsPharmaOrderKeys.PlanName]?.["filledValue"] !== ""
    ) {
      return false;
    }

    if (
      formFieldsData?.[NycbsPharmaOrderKeys.PatientRxBin]?.["filledValue"] ===
        "" ||
      formFieldsData?.[NycbsPharmaOrderKeys.PatientRxGroup]?.["filledValue"] ===
        "" ||
      formFieldsData?.[NycbsPharmaOrderKeys.PatientRxPcn]?.["filledValue"] ===
        ""
    ) {
      return true;
    }

    return false;
  };

  const showPbm = useMemo(() => {
    if (singleCmmOrderData?.[NycbsPharmaOrderKeys.PatientRxBin] === "") {
      return true;
    }
    return false;
  }, [singleCmmOrderData]);
  return (
    <div
      className={`info-banner__container mb-4 flex justify-between rounded bg-secondaryYellow-11 p-3 ${
        checkIfAnyInsuranceDataMising() ? "border border-red-500" : ""
      }`}
    >
      <div className="info-banner__heading">
        <div className="info-banner__heading-header text-body font-semibold">
          {formFieldsData?.[NycbsPharmaOrderKeys.FormName]?.["filledValue"]
            ? formFieldsData?.[NycbsPharmaOrderKeys.FormName]?.["filledValue"]
            : "Please select a PA form"}
        </div>
        <div className="info-banner__heading-header text-small font-normal italic">
          {" "}
          {formFieldsData?.[NycbsPharmaOrderKeys.FormNameInside]?.[
            "filledValue"
          ]
            ? `(${
                formFieldsData?.[NycbsPharmaOrderKeys.FormNameInside]?.[
                  "filledValue"
                ]
              })`
            : ""}
        </div>

        {singleCmmOrderData?.type === "External" ? (
          <>
            {" "}
            <div className="info-banner__heading-sub-header text-h12 font-regular">
              <span>{`Type: ${singleCmmOrderData?.form_picked_flag ?? ""}`}</span>
              <span>&#160;</span>
              <span>&#8226;</span>
              <span>&#160;</span>

              <span className="insurance-state">
                &#160;
                {`Insurance State: ${findLabelFromGivenState(formFieldsData?.[NycbsPharmaOrderKeys.PatientInsuranceState]?.["filledValue"])} `}
                &#160;
              </span>
              <div>
                {showPbm ? (
                  <span className="insurance-pbm">
                    {`PBM: ${formFieldsData?.[NycbsPharmaOrderKeys.PlanName]?.["filledValue"]} `}
                    &#160;
                  </span>
                ) : (
                  <>
                    <span className="insurance-pbm">
                      {`BIN : ${formFieldsData?.[NycbsPharmaOrderKeys.PatientRxBin]?.["filledValue"]} `}
                      &#160;
                      <span>&#8226;</span>
                      &#160;
                    </span>
                    <span className="insurance-pbm">
                      {`PCN : ${formFieldsData?.[NycbsPharmaOrderKeys.PatientRxPcn]?.["filledValue"]} `}
                      &#160;
                      <span>&#8226;</span>
                      &#160;
                    </span>
                    <span className="insurance-pbm">
                      {`Group : ${formFieldsData?.[NycbsPharmaOrderKeys.PatientRxGroup]?.["filledValue"]} `}
                      &#160;
                      <span>&#8226;</span>
                      &#160;
                    </span>
                    <span className="insurance-pbm">
                      {`Member ID: ${singleCmmOrderData?.patient_member_id ?? ""}`}
                      &#160;
                    </span>
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="info-banner__heading-sub-header text-h12 font-regular">
              <span>{`Type: ${singleCmmOrderData?.form_picked_flag ?? ""}`}</span>
              <span>&#160;</span>
              <span>&#8226;</span>
              <span>&#160;</span>
              <span className="insurance-state">
                {`Insurance State: ${findLabelFromGivenState(formFieldsData?.[NycbsPharmaOrderKeys.PatientInsuranceState]?.["filledValue"])} `}
                &#160;
                <span>&#8226;</span>
              </span>
              <span className="insurance-rx-bin">
                &#160;
                {`Bin: ${singleCmmOrderData?.sftp_rx_bin ?? ""}`}
                &#160;
                <span>&#8226;</span>
              </span>
              <span className="insurance-rx-pcn">
                &#160;
                {`Member ID: ${singleCmmOrderData?.sftp_member_id ?? ""}`}
                &#160;
              </span>
            </div>
          </>
        )}
        <div className="info-banner__heading-sub-header mt-1 text-xs font-normal text-tertiaryRed-3">
          {formFieldsData?.[NycbsPharmaOrderKeys.ErrorText]?.["filledValue"] ??
            ""}
        </div>
      </div>
      <div
        className="info-banner__action text-[0.875rem] font-semibold leading-5 text-tertiaryRed-3 hover:cursor-pointer hover:font-bold"
        onClick={() => setShowModal(true)}
      >
        Report Inaccuracy
      </div>
      {showModal && (
        <InsuranceDetailsErrorModal
          onCloseModal={() => setShowModal(() => false)}
          onSuccessfullSave={() => {
            setShowModal(() => false);
            setShowInsuranceModal(() => true);
          }}
        />
      )}
      {showInsuranceModal && (
        <InsuranceModal
          isModalOpen={(value) => setShowInsuranceModal(value)}
          id={singleCmmOrderData?.[NycbsPharmaOrderKeys.Identifier] ?? ""}
          docId={props.docId}
        />
      )}
    </div>
  );
};

export default InsuranceInfoBanner;
